import {int, unitValue} from "@opendaw/lib-std"
import {PrimitiveValues} from "@opendaw/lib-box"
import {CubedDeviceBox, ModulationBox, RandomModulatorBox} from "@opendaw/studio-boxes"
import {
    AutomatableParameterFieldAdapter,
    CubedDeviceBoxAdapter,
    Modulators,
    RandomModulatorBoxAdapter
} from "@opendaw/studio-adapters"
import {Project} from "@opendaw/studio-core"
import {Rng} from "@/generate/Rng"

type Parameter = AutomatableParameterFieldAdapter<PrimitiveValues>

export type MotionOptions = {
    seed: int
    enabled: boolean
}

type LaneSpec = {
    name: string
    parameter: (adapter: CubedDeviceBoxAdapter) => Parameter
    // A unipolar lane rests at zero and its modulator owns the whole dial. A bipolar one pushes and pulls
    // around a resting value the device keeps, which is how resonance sits high and still reaches both ends.
    bipolar?: boolean
    depth?: unitValue
}

type Lane = {spec: LaneSpec, box: RandomModulatorBox, modulation: ModulationBox}

// Every knob is owned by its modulator: the device stores 0 and a unipolar random walk at full depth is the
// whole dial, 0 to 100 %, fully smoothed so it never steps.
const Specs: ReadonlyArray<LaneSpec> = [
    {name: "cutoff", parameter: adapter => adapter.namedParameter.cutoff},
    // Resonance rests at BassLine.Resonance and swings +/- that much: it still touches zero, but everything
    // above the midpoint clamps at the top, so it spends most of its time wide open.
    {name: "resonance", parameter: adapter => adapter.namedParameter.resonance, bipolar: true, depth: 0.75},
    {name: "env-mod", parameter: adapter => adapter.namedParameter.envMod},
    {name: "decay", parameter: adapter => adapter.namedParameter.decay},
    {name: "accent", parameter: adapter => adapter.namedParameter.accent}
]

export class Motion {
    // Free-running, off the grid, one seeded rate per knob so they never line up.
    static readonly SlowestHz = 0.2
    static readonly FastestHz = 0.5
    // A raw uniform draw practically never lands on a rail, so a whole track can pass without the filter
    // ever closing or opening up. Quantising the targets to 0/25/50/75/100 % puts both ends back in reach
    // (~88 % of tracks touch each within a minute) while the smoothing still glides through everything between.
    static readonly Levels = 5

    static create(project: Project, device: CubedDeviceBox, options: MotionOptions): Motion {
        const adapter = project.boxAdapters.adapterFor(device, CubedDeviceBoxAdapter)
        const lanes = project.editing.modify(() => Specs.map((spec, index): Lane => {
            const box = Modulators.createRandom(project, spec.name)
            box.index.setValue(index)
            const modulation = Modulators.assign(project, box, spec.parameter(adapter).modulationTarget, 1.0)
            return {spec, box, modulation}
        })).unwrap("motion")
        const motion = new Motion(project, lanes)
        // Written BEFORE the worklet starts so the settings travel in the engine's initial graph dump.
        motion.write(options)
        return motion
    }

    readonly #project: Project
    readonly #lanes: ReadonlyArray<Lane>

    constructor(project: Project, lanes: ReadonlyArray<Lane>) {
        this.#project = project
        this.#lanes = lanes
    }

    write({seed, enabled}: MotionOptions): void {
        this.#project.editing.modify(() => this.#lanes.forEach(({spec, box, modulation}, index) => {
            const rng = new Rng(seed + index * 5171)
            box.enabled.setValue(enabled)
            box.rateSync.setValue(0)
            box.rateAbsolute.setValue(Motion.SlowestHz + rng.next() * (Motion.FastestHz - Motion.SlowestHz))
            box.phase.setValue(0.0)
            box.bipolar.setValue(spec.bipolar === true)
            box.amount.setValue(1.0)
            box.smooth.setValue(1.0)
            box.loop.setValue(0)
            box.levels.setValue(Motion.Levels)
            box.seed.setValue(rng.nextInt(0, RandomModulatorBoxAdapter.MaxSeed))
            modulation.depth.setValue(spec.depth ?? 1.0)
        }))
    }
}
